import styles from './index.module.css'

import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { getAggregatedSearchNextPage } from '@disco/state-management/actions/SearchActions'
import { AGGREGATED_SEARCH } from '@disco/constants/FeatureTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getTracksByIds } from '@disco/state-management/selectors/tracks'
import PaneHeader from 'components/explorer/pane/pane-header'
import Button from 'components/ui/button'
import { AGGREGATED_SEARCH_COUNT } from '@disco/constants/search'
type Props = {
  renderSearchResults: (
    searchResults: any[],
    isLoading: boolean,
    handleOnLoadClick: () => void,
    nextPageUrl?: string,
  ) => React.ReactNode
  subPaneOpen: boolean
  setSubPaneOpen: (open: boolean) => void
}

const getResultText = (
  count: number,
  isSearching: boolean,
  isOpen: boolean,
) => {
  if (isSearching) return `Searching...`
  if (count === 0) return `No results found`

  if (!isOpen) {
    if (count === 1) return `1 result`
    return `${count} results`
  }

  if (count === 1) return `Showing 1 result`
  if (count < AGGREGATED_SEARCH_COUNT) return `Showing all ${count} results`
  return `Showing first ${count} results`
}

const AggregatedSearch = ({
  renderSearchResults,
  subPaneOpen = false,
  setSubPaneOpen,
}: Props): null | React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const {
    searchOpen,
    searchQuery,
    searching,
    type,
    aggregatedSearch: {
      isSearching,
      isLoadingNextPage,
      searchResults,
      nextPageUrl,
    },
  } = useSelector(state => state.search)
  const tracks = useSelector(state =>
    getTracksByIds(state, {
      ids: searchResults.map(({ id }) => id),
    }),
  )
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const aggregatedSearchEnabled = useFeatureEnabled(AGGREGATED_SEARCH)

  const handleLoadMoreClick = () => {
    dispatch(getAggregatedSearchNextPage(nextPageUrl))
  }

  const toggle = () => {
    const open = !subPaneOpen
    setSubPaneOpen(open)
    // scroll the inner pane to this button
    if (open)
      setTimeout(() => {
        buttonRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 0)
  }

  const showsOnlyTracks = !type || type === TRACK
  if (
    !aggregatedSearchEnabled ||
    !searchOpen ||
    (!searchQuery && !searching) ||
    !showsOnlyTracks
  ) {
    return null
  }

  if (isSearching) return null
  return (
    <div className={styles.container}>
      <PaneHeader
        title="Results from across DISCO"
        caption={getResultText(
          tracks.length,
          isSearching || isLoadingNextPage,
          subPaneOpen,
        )}
        dark={!subPaneOpen}
      >
        <Button
          ref={buttonRef}
          onClick={toggle}
          icon="arrow-down"
          style={{
            transform: subPaneOpen ? 'none' : 'rotate(180deg)',
          }}
          appearance={subPaneOpen ? 'icon' : 'transparent'}
        />
      </PaneHeader>
      {subPaneOpen &&
        renderSearchResults(
          tracks,
          isSearching || isLoadingNextPage,
          handleLoadMoreClick,
          nextPageUrl,
        )}
    </div>
  )
}

export default AggregatedSearch
